<!-- xlsx.js (C) 2013-present  SheetJS -- http://sheetjs.com -->
<template>
  <div @dragenter="dragEnter" @dragover="dragEnter" @drop="_drop" style="position: relative;">
    <CJumbotron class="info-box">
      <h1 class="display-4">{{ $t('domain.BatchImportOfDomainNames') }}</h1>

      <p>{{ $t('message.batch_domain_import') }} </p>

      <button id="download_import_file" :disabled="!data.length" class="btn btn-outline-primary" @click="_export">
        <CIcon name="cil-cloud-download" />&nbsp;
        {{ $t('DownloadImportFile') }}
      </button>

      <button id="upload_import_file" class="btn btn-primary" style="margin-left: 8px;" @click="handleSelectFile">
        <CIcon name="cil-cloud-upload" />&nbsp;
        {{ $t('UploadImportFile') }}
      </button>

      <input type="file" style="display: none;" @change="_change" ref="fileInput">

      <p style="margin-top: 8px;">{{ $t('message.EditFileDescription') }}： </p>
      <ol>
        <li>{{ $t('message.BatchImportDescriptionItem1') }}</li>
        <li>{{ $t('message.BatchImportDescriptionItem2') }}</li>
        <li>{{ $t('message.BatchImportDescriptionItem3') }}</li>
        <li>{{ $t('message.BatchImportDescriptionItem4') }}</li>
      </ol>

    </CJumbotron>

    <CCard>
      <CCardHeader>
        <slot name="header">
          <CIcon name="cil-grid" />
          {{ $t('FileExample') }}
        </slot>
      </CCardHeader>
      <CCardBody>
        <CAlert color="info" v-if="isDomainImportInProgress && data.length" class="mb-3">
          {{ $t('message.import_table_order_info') }}
        </CAlert>
        <ImportProgressBar
          v-if="isDomainImportInProgress && data.length"
          :total="data.length"
          :completed="importProgressCompleted"
          :failed="importProgressFailed"
          :enqueued="importProgressEnqueued"
          :waiting="importProgressWaiting"
        />
        <CDataTable :items="displayData" :fields="fields" :items-per-page="100" pagination>
          <template #remarks="{item}">
            <td v-if="item._errors">
              <ul v-if="item._errors.length > 1" class="error-list">
                <li v-for="e in item._errors" :key="e">
                  <CIcon name="cil-warning" />&nbsp;
                  {{ e }}
                </li>
              </ul>
              <span v-else-if="item._errors.length === 1">
                <CIcon name="cil-warning" />&nbsp;
                {{ item._errors[0] }}
              </span>
            </td>
            <td v-else-if="item._result">
              <CIcon name="cil-check-circle" />&nbsp;
              {{ item._result }}
            </td>
            <td v-else-if="item.saving_shadow_domains">
              <CSpinner size="sm" />
              {{ $t('message.SavingShadowDomains') }}
            </td>
            <td v-else-if="item.loading">
              <CSpinner size="sm" />
              {{ $t('processing') }}
            </td>
            <td v-else-if="item.redeploy">
              <CSpinner size="sm" />
              {{ $t('message.RedeployingDomain') }}
            </td>
            <td v-else-if="item.onqueue">
              <CSpinner size="sm" />
            </td>
            <td v-else>
              &nbsp;
            </td>
          </template>
          <template #status={item}>
            <td>
              {{ $t(item.status) }}
            </td>
          </template>
          <template #auto_generate_cert="{item}">
            <td>
              <CIcon v-if="item.auto_generate_cert" name="cil-check-circle" />
              <CIcon v-else name="cil-x-circle" />
            </td>
          </template>
          <template #protocol="{item}">
            <td>
              <CBadge :color="item.protocol === 'https' ? 'success' : 'warning'">
                {{ item.protocol }}
              </CBadge>
            </td>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>

    <div class="row">
      <div class="col-xs-12"></div>
    </div>
    <div class="row">
      <div class="col-xs-12">

      </div>
    </div>

    <div class="drag-drop-indicator" :class="{ activeDrag: hasDraggedItem }"></div>
  </div>
</template>

<style scoped>
.info-box {
  padding-top: 1rem !important;
}
ul.error-list {
  list-style: none;
  padding-left: 0;
}

.drag-drop-indicator.activeDrag {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: steelblue;
  opacity: 0.2;
}
</style>

<script>
import XLSX from "xlsx";
import { Websocket } from "@/plugins/websocket";
import { $i18n } from '@/plugins/i18n';
import DomainValidationMixin from '@/utilities/DomainValidationMixin';
import { ExportDomainMixin, COLUMN_KEYS } from '@/mixins/ExportDomainMixin';
import { DomainAction } from '@/utilities/websocket';
import { WebSocketBatchQueue } from '@/utilities/WebSocketBatchQueue';
import { LIMIT_BATCH_IMPORT, MAX_BATCH_IMPORT } from '@/utilities/batch';
import { TAG_NAMES, INVALID_IP_RANGES, buildConnectingIpHeaderPlugin  } from '@/utilities/constants';
import { Route } from '@/models/Route';
import * as ipaddr from 'ipaddr.js';
import ImportProgressBar from './ImportProgressBar.vue';

export default {
  mixins: [DomainValidationMixin, ExportDomainMixin],
  components: { ImportProgressBar },
  mounted() {
    this.openWs();
    this.initImportQueue();
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  data() {
    return {
      message: "",
      hasDraggedItem: false,
      clearDragFlagRef: null,
      onProgressCreate: [],
      onProgressDelete: [],
      mutatedData: [],
      isDomainImportInProgress: false,
      hasEnqueued: false,
      queuePendingCount: 0,
      queueInFlightCount: 0,
    };
  },
  computed: {
    data: {
      get() {
        if (this.mutatedData.length === 0) {
          return [{
            [COLUMN_KEYS.NAME]: 'v03.example.com',
            [COLUMN_KEYS.ORIGIN_IP]: '8.8.8.8',
            [COLUMN_KEYS.PROTOCOL]: 'http',
            [COLUMN_KEYS.SHADOW_DOMAINS]: 'xyz.example.com shadow.example.com',
            [COLUMN_KEYS.STATUS]: this.$t('online'),
            [COLUMN_KEYS.AUTO_GENERATE_CERT]: false,
            [COLUMN_KEYS.REMARK]: 'notes'
          },
          {
            [COLUMN_KEYS.NAME]: 'v04.example.com',
            [COLUMN_KEYS.ORIGIN_IP]: '8.8.8.8',
            [COLUMN_KEYS.PROTOCOL]: 'https',
            [COLUMN_KEYS.SHADOW_DOMAINS]: '',
            [COLUMN_KEYS.STATUS]: this.$t('disabled'),
            [COLUMN_KEYS.AUTO_GENERATE_CERT]: true,
            [COLUMN_KEYS.REMARK]: 'notes'
          },
          {
            [COLUMN_KEYS.NAME]: 'v05.example.com',
            [COLUMN_KEYS.ORIGIN_IP]: '8.8.8.8, 8.8.4.4',
            [COLUMN_KEYS.PROTOCOL]: 'http',
            [COLUMN_KEYS.SHADOW_DOMAINS]: '',
            [COLUMN_KEYS.STATUS]: this.$t('deleted'),
            [COLUMN_KEYS.AUTO_GENERATE_CERT]: false,
            [COLUMN_KEYS.REMARK]: 'notes'
          }]
        }
        return this.mutatedData;
      },
      set(data) {
        this.mutatedData = data;
      }
    },
    displayData() {
      return this.sortRowsByQueueOrder(this.data);
    },
    importProgressCompleted() {
      const rows = this.data ?? [];
      return rows.filter((r) => r[COLUMN_KEYS.RESULT]).length;
    },
    importProgressFailed() {
      const rows = this.data ?? [];
      return rows.filter((r) => r[COLUMN_KEYS.ERRORS] && r[COLUMN_KEYS.ERRORS].length).length;
    },
    importProgressEnqueued() {
      if (!this.hasEnqueued) return 0;
      return this.queueInFlightCount;
    },
    importProgressWaiting() {
      if (!this.hasEnqueued) return this.data?.length ?? 0;
      return this.queuePendingCount;
    },
    cname: {
      get() {
        return this.$store.state.cname;
      },
      set(value) {
        this.$store.state.cname = value;
      }
    },
    domain_ws_import: {
      get() {
        return this.$store.state.websocket.domain_ws_import;
      },
      set(value) {
        this.$store.state.websocket.domain_ws_import = value;
      }
    },    
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDomainImportInProgress) {
      const answer = window.confirm($i18n('ImportClosePageAlert'));
      if (answer) {
        next()
      } else {
        next(false)
      }
    } else{
      next()
    }
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.closeWs();
  },
  methods: {
    sortRowsByQueueOrder(rows) {
      if (!rows?.length) return rows ?? [];
      const disabled = [];
      const deleted = [];
      const create = [];
      for (const row of rows) {
        const s = (row[COLUMN_KEYS.STATUS] ?? row.status ?? '').toLowerCase();
        if (s === 'disabled') disabled.push(row);
        else if (s === 'deleted') deleted.push(row);
        else create.push(row);
      }
      return [...disabled, ...deleted, ...create];
    },
    isValidIpAddress(ipWithPort) {
      try {
        let ip = ipWithPort;
        let port = null;

        // Handle IPv6 with port (e.g., [::1]:443)
        if (ip.startsWith('[')) {
          const match = ip.match(/^\[([^\]]+)](?::(\d+))?$/);
          if (!match) {
            return false;
          }
          ip = match[1];
          port = match[2];
        } else {
          const parts = ipWithPort.split(':');
          if (parts.length > 2) {
            // IPv6 without brackets
            ip = ipWithPort;
          } else {
            ip = parts[0];
            port = parts[1];
          }
        }

        // Validate port
        if (port !== undefined && port !== null) {
          const portNumber = parseInt(port, 10);
          if (isNaN(portNumber) || portNumber < 1 || portNumber > 65535) {
            return false;
          }
        }

        // Check if it's a valid IP
        if (ipaddr.isValid(ip)) {
          const parsed = ipaddr.parse(ip);
          const range = parsed.range();
          if (INVALID_IP_RANGES.includes(range)) {
            return false;
          }
          return true;
        }

        // If not an IP, assume domain and validate domain pattern
        const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z]{2,}$/;
        if (domainRegex.test(ip)) {
          return true;
        }

        return false;

      } catch (e) {
        return false;
      }
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async openWs() {
      this.domain_ws_import = new Websocket(`domain/`, null, {
        onopen: () => {
          console.info('Successfully connected to the domain websocket server...');
        },
      });

      this.domain_ws_import.onMessage = (event, result) => {
        const handlers = {
          [DomainAction.DOMAIN_GET]: () => {
            const row = result.data?.row;
            if (!row) return;
            if (row.status.toLowerCase() === 'disabled' && result.data.domains?.length > 0) {
              const [domain, shouldSave, updateDomainStatus] = this.getDomainSaveUpdateStatus(result);
              this.saveOrUpdateFromWSResponse(row, domain, shouldSave, updateDomainStatus);
            } else if (row.status.toLowerCase() === 'deleted') {
              this.updateStatus(row, "loading", true);
              if (result.data.domains?.length > 0) {
                this.sendDeploy(DomainAction.DOMAIN_DELETE, {
                  route_id: result.data.domains[0].domain.id,
                  cname: this.$store.getters.cname,
                  row,
                });
              } else {
                this.updateStatus(row, COLUMN_KEYS.ERRORS, [this.$t('message.domainNotFoundNoDeletionNeeded')]);
                this.completeImportQueueForRow(row);
              }
            } else {
              const [domain, shouldSave, updateDomainStatus] = this.getDomainSaveUpdateStatus(result);
              this.saveOrUpdateFromWSResponse(row, domain, shouldSave, updateDomainStatus);
            }
          },
          [DomainAction.DOMAIN_CREATE]: () => {
            const row = result.data?.row ?? this.getImportRowFromResponse(result);
            if (row) {
              if (result.data?.error) this.applyImportRowError(row, result.data.error);
              else {
                this.updateStatus(row, "loading", false);
                this.updateStatus(row, COLUMN_KEYS.RESULT, this.$t('message.domain_created'));
              }
            }
            this.handleImportRowCompletion(row, result);
          },
          [DomainAction.DOMAIN_UPDATE]: () => {
            const row = result.data?.row ?? this.getImportRowFromResponse(result);
            if (row) {
              if (result.data?.error) this.applyImportRowError(row, result.data.error);
              else {
                this.updateStatus(row, "loading", false);
                this.updateStatus(row, COLUMN_KEYS.RESULT, this.$t('message.domain_updated'));
              }
            }
            this.handleImportRowCompletion(row, result);
          },
          [DomainAction.PAUSE_RESTORE_DOMAIN]: () => {
            // Backend typically returns result.data.row; getImportRowFromResponse fallback uses result.data.domain.
            const row = result.data?.row ?? this.getImportRowFromResponse(result);
            if (row) {
              this.updateStatus(row, "loading", false);
              if (result.data.message?.includes('updated successfully')) {
                this.updateStatus(row, COLUMN_KEYS.RESULT, this.$t('message.domain_disabled'));
              } else {
                this.updateStatus(row, COLUMN_KEYS.ERRORS, [this.$t('message.failed_to_deactivate_domain')]);
              }
            }
            this.completeImportQueueForRow(row);
          },
          [DomainAction.DOMAIN_DELETE]: () => {
            const row = result.data?.row ?? this.getImportRowFromResponse(result);
            if (row) {
              this.updateStatus(row, "loading", false);
              if (result.data.message?.includes('deleted successfully')) {
                this.updateStatus(row, COLUMN_KEYS.RESULT, this.$t('message.SuccessfullyDeleted'));
              } else if (result.data.error) {
                this.updateStatus(row, COLUMN_KEYS.ERRORS, [this.$t('message.domainNotFoundNoDeletionNeeded')]);
              }
            }
            this.completeImportQueueForRow(row);
          },
        };

        const fn = handlers[result.action];
        if (fn) fn();

        const msg = result.data?.message;
        if (msg && !msg.includes('successfully')) {
          const type = result.data?.error ? 'warning' : 'info';
          this.flash(msg, type, { timeout: 4000 });
        }
      };
    },
    handleBeforeUnload(e) {
      if (this.isDomainImportInProgress) {
        e.preventDefault();
        e.returnValue = this.$t('message.import_in_progress_alert') || '';
      }
    },
    closeWs() {
      if (this.domain_ws_import) this.domain_ws_import.close();
    },
    initImportQueue() {
      this.importQueue = new WebSocketBatchQueue({
        concurrency: LIMIT_BATCH_IMPORT,
        onSend: (rows) => {
          rows.forEach((row) => {
            this.sendDeploy(DomainAction.DOMAIN_GET, {
              domain_name: row[COLUMN_KEYS.NAME].trim(),
              row,
            });
          });
        },
        onCompleteOne: () => {
          this.queuePendingCount = this.importQueue?.pendingCount ?? 0;
          this.queueInFlightCount = this.importQueue?.inFlightCount ?? 0;
        },
      });
    },
    /** Apply error status to import row (hosts, upstream, service_protocol, or generic). */
    applyImportRowError(row, error) {
      this.updateStatus(row, 'loading', false);
      if (error?.hosts) this.updateStatus(row, COLUMN_KEYS.ERRORS, [error.hosts[0]]);
      else if (error?.upstream) this.updateStatus(row, COLUMN_KEYS.ERRORS, [error.upstream[0]]);
      else if (error?.service_protocol) this.updateStatus(row, COLUMN_KEYS.ERRORS, [error.service_protocol[0]]);
      else this.updateStatus(row, COLUMN_KEYS.ERRORS, [error]);
    },
    completeImportQueueForRow(row) {
      if (!this.importQueue || this.importQueue.inFlightCount === 0) return;
      const matcher = row ? this.matchQueueItemByRow(row) : undefined;
      this.importQueue.completeOne(matcher);
    },
    /** Resolve import row from CREATE/UPDATE response when result.data.row is missing. */
    getImportRowFromResponse(result) {
      const domain = result.data?.domain;
      if (!domain) return null;
      const domainName = (domain.hosts?.[0] || domain.domain_name || '')
        .replace(/-/g, '.')
        .toLowerCase();
      if (!domainName) return null;
      const norm = (s) => (s || '').replace(/-/g, '.').trim().toLowerCase();
      return this.data?.find((item) => norm(item[COLUMN_KEYS.NAME] || item.name) === domainName) ?? null;
    },
    /**
     * On CREATE/UPDATE success: if row needs disable → send PAUSE_RESTORE; else completeOne.
     * Skip PAUSE_RESTORE when backend status already Disabled (no action 9 needed).
     */
    handleImportRowCompletion(row, result) {
      if (!this.importQueue?.inFlightCount) return;
      const status = (row?.[COLUMN_KEYS.STATUS] ?? row?.status ?? '').toLowerCase();
      const isSuccess = !result.data?.error;
      const backendStatus = (result.data?.row?.status ?? '').toLowerCase();
      const backendIsDisabled = backendStatus === 'disabled' || result.data?.paused === true;

      if (row && isSuccess && status === 'disabled' && !backendIsDisabled) {
        this.sendDeploy(DomainAction.PAUSE_RESTORE_DOMAIN, {
          hosts: [(row[COLUMN_KEYS.NAME] || row.name || '').trim()],
          paused: true,
          row,
        });
      } else {
        this.completeImportQueueForRow(row);
      }
    },
    /** Normalize domain for comparison (backend may use hyphens, we use dots). */
    normalizeDomainForMatch(name) {
      return (name || '').replace(/-/g, '.').trim().toLowerCase();
    },
    /** Match queue item by row. Uses _id; falls back to domain name (hyphen/dot normalized). */
    matchQueueItemByRow(row) {
      if (!row) return () => true;
      const targetId = row._id;
      const targetName = this.normalizeDomainForMatch(row[COLUMN_KEYS.NAME] || row.name);
      return (item) => {
        if (targetId != null && item?._id === targetId) return true;
        if (targetName && item) {
          return this.normalizeDomainForMatch(item[COLUMN_KEYS.NAME] || item.name) === targetName;
        }
        return false;
      };
    },
    sendDeploy(action, domains) {
      this.domain_ws_import.sendMessage(action, domains);
    },
    /**
     * Enqueue all rows into the single import queue. Each row flows:
     * DOMAIN_GET → DOMAIN_CREATE/UPDATE → (optional) PAUSE_RESTORE_DOMAIN or DOMAIN_DELETE.
     * On chain success, completeOne vacates the slot and the next row's chain starts immediately.
     */
    async makeCallToAPI(rows) {
      const disabledRows = [];
      const deletedRows = [];
      const createRows = [];

      for (const row of rows) {
        const status = row[COLUMN_KEYS.STATUS].toLowerCase();
        row[COLUMN_KEYS.NAME] = row[COLUMN_KEYS.NAME].toLowerCase();
        row[COLUMN_KEYS.SHADOW_DOMAINS] = row[COLUMN_KEYS.SHADOW_DOMAINS].toLowerCase();
        row[COLUMN_KEYS.AUTO_GENERATE_CERT] = row[COLUMN_KEYS.AUTO_GENERATE_CERT] === 'true';

        if (status === 'disabled') disabledRows.push(row);
        else if (status === 'deleted') deletedRows.push(row);
        else createRows.push(row);
      }

      const orderedRows = [...disabledRows, ...deletedRows, ...createRows];
      if (orderedRows.length === 0) return;

      orderedRows[0].clear_redis = true;
      orderedRows[orderedRows.length - 1].clear_redis = true;
      orderedRows[orderedRows.length - 1].hit_broadcast = true;

      this.onProgressDelete = deletedRows;
      this.onProgressCreate = createRows;

      const needsClearRedisDelay = createRows.some((r) => r.clear_redis);
      if (needsClearRedisDelay) {
        this.flash(this.$t('message.import_clearing_cache'), 'info', { timeout: 5500 });
        await this.sleep(5000);
      }

      await new Promise((resolve) => {
        // Temporarily override onDrain to resolve when this batch drains (no restoration needed).
        const prevOnDrain = this.importQueue.onDrain;
        this.importQueue.onDrain = () => {
          prevOnDrain?.();
          resolve();
        };
        this.hasEnqueued = true;
        this.importQueue.enqueueMany(orderedRows);
        this.queuePendingCount = this.importQueue.pendingCount;
        this.queueInFlightCount = this.importQueue.inFlightCount;
      });
    },
    async startImport() {
      if (this.isDomainImportInProgress === false) {
        this.isDomainImportInProgress = true;
        this.flash(this.$t('message.upload_in_progress'), 'info', { timeout: 4000 });
        for (var k = 0; (k < this.data.length); k++) {
            this.updateStatus(this.data[k], "loading", true);
        }

        await this.makeCallToAPI(this.displayData);

        const succeeded = this.data.filter((row) => row[COLUMN_KEYS.RESULT]).length;
        const failed = this.data.filter((row) => row[COLUMN_KEYS.ERRORS]?.length).length;
        if (failed > 0) {
          this.flash(
            this.$t('message.import_completed_counts', { succeeded, failed }),
            'warning',
            { timeout: 6000 }
          );
        } else {
          this.flash(this.$t('message.UploadCompleted'), 'success', { timeout: 4000 });
        }

        this.isDomainImportInProgress = false;
        this.hasEnqueued = false;
        this.queuePendingCount = 0;
        this.queueInFlightCount = 0;
      }
    },
    updateStatus(row, status, message) {
      if (row) {
        const _data = this.data.map((datum) => {
          if (datum._id === row._id) {
            const _datum = { ...datum };
            if (!_datum[status]) {
              _datum[status] = {};
            }

            _datum[status] = message;

            return _datum;
          }

          return datum;
        });

        this.mutatedData = _data;
      }
    },
    async pauseRestoreDoain(row) {

      let isPaused = false;

      if (row.status.toLowerCase() == 'disabled') {
        isPaused = true;
      } else {
        isPaused = false;
      }

      this.sendDeploy(DomainAction.PAUSE_RESTORE_DOMAIN, {
        "hosts": [row[COLUMN_KEYS.NAME].trim()],
        "paused": isPaused,
        "row": row
      });
    },
    updateDomain(createDomainData, row) {
      try {
          this.sendDeploy(DomainAction.DOMAIN_UPDATE, createDomainData);
          return true
      } catch (rawError) {
        const error = rawError.error;
        if (error && error.response && error.response.data && error.response.data.field_errors) {
          if (error.response.data.field_errors.hosts) {
            if (error.response.data.field_errors.hosts[0].includes('already exist')) {
              this.updateStatus(row, COLUMN_KEYS.ERRORS, [this.$t('message.domain_already_exist')]);
              if (this.importQueue?.inFlightCount > 0) {
                this.importQueue.completeOne(this.matchQueueItemByRow(row));
              }
              return;
            }
          }
        }
        this.updateStatus(row, COLUMN_KEYS.ERRORS, [this.$t('message.PleaseCheckTheDomainNameFirst')]);
        if (this.importQueue?.inFlightCount > 0) {
          this.importQueue.completeOne(this.matchQueueItemByRow(row));
        }
        return;

      }
    },
    getDomainSaveUpdateStatus(response) {
      let shouldSave = false;
      let updateDomainStatus = false;
      let domain;

      try {
        if (response.data.domains && response.data.domains[0]) {
          domain = response.data.domains[0];
          shouldSave = true;
          updateDomainStatus = true;
        } else {
          shouldSave = true;
        }
      } catch {
        shouldSave = false;
      }

      return [domain, shouldSave, updateDomainStatus];
    },
    async saveOrUpdateFromWSResponse(row, domain, shouldSave, updateDomainStatus) {
      function cleanInput(val) {
        return (val || '').trim();
      }

      const hasAutoGenerateCertificate = row[COLUMN_KEYS.AUTO_GENERATE_CERT];
      const rawShadowDomain = (row[COLUMN_KEYS.SHADOW_DOMAINS] || '').trim();
      let shadowDomains = rawShadowDomain.length ? rawShadowDomain.split(',') : [];

      shadowDomains = shadowDomains.join().split(/\s|,/).filter(Boolean);
      shadowDomains = shadowDomains.join().split(/""|,/).filter(Boolean);
      let shadowdomainsList = shadowDomains;

      if (shouldSave) {
        this.updateStatus(row, "loading", true);
        row.name = cleanInput(row.name);

        if (row.name === '' || !this.domain_validator(row.name)) {
          this.updateStatus(row, COLUMN_KEYS.ERRORS, [this.$t('message.PleaseCheckTheDomainNameFirst')]);
          if (this.importQueue?.inFlightCount > 0) {
            this.importQueue.completeOne(this.matchQueueItemByRow(row));
          }
          return;
        }

        if (shadowDomains.length) {
          const shadowDomainValidationErrors = [];
          for (let s of shadowDomains) {
            if (!this.shadow_domain_validator(s)) {
              shadowDomainValidationErrors.push(this.$t('message.InvalidShadowDomains', [s]));
            }
          }

          if (shadowDomainValidationErrors.length) {
            this.updateStatus(row, COLUMN_KEYS.ERRORS, shadowDomainValidationErrors);
            if (this.importQueue?.inFlightCount > 0) {
              this.importQueue.completeOne(this.matchQueueItemByRow(row));
            }
            return;
          }
        }

        shadowDomains = shadowDomains.map(item => {
          return { name: item };
        });
        
        const targets = [];
        const origin_ips = row.origin_ip.split(',');
        origin_ips.forEach((origin) => {
          const origin_ip_split = origin.split(':');
          if (origin_ip_split.length === 1) {
            origin += ':80';
          } 
          targets.push({"target": origin});
        });

        let isPaused = false;

        if (row.status.toLowerCase() == 'disabled') {
          isPaused = true;
        } else {
          isPaused = false;
        }

        const createDomainData = {
          "domain": {
            "hosts": [row.name],
            "protocols": ["http", "https"],
            "upstream": {
              "targets": targets 
            },
            "service": {
              "protocol": row.protocol,
              "tags": []
            },
            "plugins": []
          },
          "meta": {
            "cname": this.$store.getters.cname,
            "description": row.remark
          },
          "paused": isPaused,
          "row": row
        }

        if (hasAutoGenerateCertificate) {
          createDomainData['domain']['service']['tags'] = [TAG_NAMES.HAS_AUTO_GENERATE_CERTIFICATE];
        }

        if (row.shadow_domains) {
          createDomainData['domain']['hosts'] = createDomainData['domain']['hosts'].concat(shadowdomainsList);
        }

        // Give imported domains the same plugin baseline as the single-domain create form:
        // Route.getPlugins() on an empty Route is exactly what EditDomain seeds (gaius-proxy-cache
        // with the default cache_paths/response_code/strategy), plus the client-IP passthrough
        // header. Neither needs a CSV column — both are fixed defaults, not per-row data. Sourcing
        // the cache defaults from Route rather than copying them keeps import in sync if they
        // change. CREATE only — on update this payload's empty `plugins` is left alone rather
        // than being turned into a list that would clobber the existing domain's plugins.
        if (!updateDomainStatus) {
          createDomainData['domain']['plugins'] = [
            ...new Route().getPlugins(),
            buildConnectingIpHeaderPlugin(),
          ];
        }

        if (updateDomainStatus) {
          createDomainData['route_id'] = domain.domain.id;

          this.updateDomain(createDomainData, row);
          return;
        } else {
          try {
            this.sendDeploy(DomainAction.DOMAIN_CREATE, createDomainData);
          } catch (rawError) {
            const error = rawError.error;
            if (error && error.response && error.response.data && error.response.data.field_errors) {
              if (error.response.data.field_errors.hosts) {
                if (error.response.data.field_errors.hosts[0].includes('already exist')) {
                    this.updateStatus(row, COLUMN_KEYS.ERRORS, [this.$t('message.domain_already_exist')]);
                  if (this.importQueue?.inFlightCount > 0) {
                    this.importQueue.completeOne(this.matchQueueItemByRow(row));
                  }
                  return;
                }
              }
            }
            this.updateStatus(row, COLUMN_KEYS.ERRORS, [this.$t('message.PleaseCheckTheDomainNameFirst')]);
            if (this.importQueue?.inFlightCount > 0) {
              this.importQueue.completeOne(this.matchQueueItemByRow(row));
            }
            return;

          }
          return;
        }
      }

      const shouldReactivate = domain?.domain?.status === 'Deactivated';

      if (shouldReactivate) {
        let resumeResponse;
        try {
          this.sendDeploy(DomainAction.PAUSE_RESTORE_DOMAIN, {
            hosts: [row[COLUMN_KEYS.NAME]?.trim() || row.name],
            paused: false,
            row: row
          });
        } catch (errors) {
          this.updateStatus(row, COLUMN_KEYS.ERRORS, errors);
          if (this.importQueue?.inFlightCount > 0) {
            this.importQueue.completeOne(this.matchQueueItemByRow(row));
          }
          return;
        }

        if (resumeResponse && resumeResponse.status == 200) {
          this.updateStatus(row, COLUMN_KEYS.RESULT, this.$t('message.domain_reactivated'));
        } else {
          this.updateStatus(row, COLUMN_KEYS.ERRORS, [this.$t('message.failed_to_reactivate_domain')]);
        }
        return;
      }

      this.updateStatus(row, COLUMN_KEYS.ERRORS, [this.$t('message.domain_already_exist')]);
      if (this.importQueue?.inFlightCount > 0) {
        this.importQueue.completeOne(this.matchQueueItemByRow(row));
      }
    },
    async saveOrResume(row) {
      const domainName = row[COLUMN_KEYS.NAME].trim();

      this.sendDeploy(DomainAction.DOMAIN_GET, {
        "domain_name": domainName,
        "row": row
      });
    },
    async deactivateDomain(row) {
      this.updateStatus(row, "loading", true);

      try {
        this.sendDeploy(DomainAction.DOMAIN_GET, {
          "domain_name": row[COLUMN_KEYS.NAME].trim(),
          "row": row
        });
      } catch (errors) {
        this.updateStatus(row, COLUMN_KEYS.ERRORS, errors);
        return;
      }
      // const success = response.status == "200";
      // if (success) {
      //   this.updateStatus(row, COLUMN_KEYS.RESULT, this.$t('message.domain_disabled'));
      // } else {
      //   this.updateStatus(row, COLUMN_KEYS.ERRORS, [this.$t('message.failed_to_deactivate_domain')]);
      // }
    },
    handleSelectFile() {
      // reset selection
      this.$refs.fileInput.value = '';

      this.$refs.fileInput.click();
      this.longDomainErrorShown = false;
    },
    dragEnter(evt) {
      evt.stopPropagation();
      evt.preventDefault();

      this.clearDragFlag();

      this.hasDraggedItem = true;
      this.clearDragFlagRef = setTimeout(() => {
        this.hasDraggedItem = false;
      }, 3000);
    },
    clearDragFlag() {
      if (this.clearDragFlagRef) {
        clearTimeout(this.clearDragFlagRef);
        this.clearDragFlagRef = null;
      }
    },
    _drop(evt) {
      evt.stopPropagation();
      evt.preventDefault();

      this.clearDragFlag();
      this.hasDraggedItem = false;

      const files = evt.dataTransfer.files;
      if (files && files[0]) this._file(files[0]);
    },
    _change(evt) {
      const files = evt.target.files;
      if (files && files[0]) this._file(files[0]);
    },
    _export() {
      this.setExportImportLang();
      const sheetSampleData = [{
        [COLUMN_KEYS.NAME]: 'v03.example.com',
        [COLUMN_KEYS.ORIGIN_IP]: '8.8.8.8',
        [COLUMN_KEYS.PROTOCOL]: 'http',
        [COLUMN_KEYS.SHADOW_DOMAINS]: 'xyz.example.com shadow.example.com',
        [COLUMN_KEYS.STATUS]: this.$t('online'),
        [COLUMN_KEYS.AUTO_GENERATE_CERT]: false,
        [COLUMN_KEYS.REMARK]: "notes"
      },
      {
        [COLUMN_KEYS.NAME]: 'v04.example.com',
        [COLUMN_KEYS.ORIGIN_IP]: '8.8.8.8',
        [COLUMN_KEYS.PROTOCOL]: 'https',
        [COLUMN_KEYS.SHADOW_DOMAINS]: '',
        [COLUMN_KEYS.STATUS]: this.$t('disabled'),
        [COLUMN_KEYS.AUTO_GENERATE_CERT]: true,
        [COLUMN_KEYS.REMARK]: "notes"
      },
      {
        [COLUMN_KEYS.NAME]: 'v05.example.com',
        [COLUMN_KEYS.ORIGIN_IP]: '8.8.8.8,8.8.4.4',
        [COLUMN_KEYS.PROTOCOL]: 'http',
        [COLUMN_KEYS.SHADOW_DOMAINS]: '',
        [COLUMN_KEYS.STATUS]: this.$t('deleted'),
        [COLUMN_KEYS.AUTO_GENERATE_CERT]: false,
        [COLUMN_KEYS.REMARK]: "notes"
      }];
      this._exportDomainData(sheetSampleData);
    },
    _file(file) {
      const reader = new FileReader();

      reader.onload = e => {

        function cleanInput(val) {
          return (val.toString() || '').trim();
        }

        this.setExportImportLang();
        const bstr = e.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });

        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];

        const resultJson = XLSX.utils.sheet_to_json(ws, { header: this.exportFields.map((f) => f.key) });

        if (resultJson.length < 2) {
          this.flash(this.$t('message.empty_import_file'), 'error', { "timeout": 5000 });
          return;
        }

        let [_headerRow, ...data] = resultJson;

        Object.keys(data).forEach(key => {
          if (data[key].name === '' || data[key].name === undefined) {
            delete data[key]
          }
        });

        data = data.filter(i => i !== null);

        // AC-1949: a single batch import may contain at most MAX_BATCH_IMPORT domains.
        if (data.length > MAX_BATCH_IMPORT) {
          this.flash(this.$t('message.import_too_many_domains', { max: MAX_BATCH_IMPORT }), 'error', { "timeout": 8000 });
          return;
        }

        const hasDomainColumn = _headerRow[COLUMN_KEYS.NAME] === this.$t("domain.Domain");
        const hasOriginIpColumn = _headerRow[COLUMN_KEYS.ORIGIN_IP] === this.$t("domain.OriginIP");
        const hasStatusColumn = _headerRow[COLUMN_KEYS.STATUS] === this.$t("domain.Status");

        if (!hasDomainColumn || !hasOriginIpColumn || !hasStatusColumn) {
          this.flash(this.$t('message.import_file_no_header'), 'error', { "timeout": 8000 });
          return;
        }

        const statusAcceptedValues = [this.$t('online'), this.$t('disabled'), this.$t('deleted')].map((v) => v.toLowerCase());
        const statusValidationPassed = data.every((datum) => {
          const datumElement = datum[COLUMN_KEYS.STATUS];
          if (!datumElement)
            return false;

          return statusAcceptedValues.includes(cleanInput(datumElement).toLowerCase());
        });

        if (!statusValidationPassed) {
          this.flash(this.$t('message.allowed_status_only') + ' ' + statusAcceptedValues.join(', '), 'error', { "timeout": 8000 });
          return;
        }

        const originValidationPassed = data.every((datum) => {
          const datumElement = datum[COLUMN_KEYS.ORIGIN_IP];
          if (!datumElement)
            return false;
          return this.source_validator(cleanInput(datumElement));
        });
        if (!originValidationPassed) {
          this.flash(this.$t('message.Required_origin_ip'), 'error', { "timeout": 5000 });
          return;
        }
        const originValidationIp = data.every((datum) => {
          const originIp = cleanInput(datum[COLUMN_KEYS.ORIGIN_IP]);

          // If origin IP is empty, fail validation
          if (!originIp) {
            this.notifyError(this.$t('message.Required_origin_ip'), this.$t('message.InputDataVerificationError'));
            return false;
          }

          if (originIp.split(':').length - 1 > 1) {
            // IPv6 check
            if (!this.source_ipv6_validator(originIp)) {
              this.notifyError(this.$t('message.IsNotAValidHostnameOrIpFormat', [originIp]), this.$t('message.InputDataVerificationError'));
              return false;
            }
          } else {
            // IPv4 or hostname check
            if (!this.source_hostname_validator(originIp) && !this.source_ipv4_validator(originIp)) {
              this.notifyError(this.$t('message.IsNotAValidHostnameOrIpFormat', [originIp]), this.$t('message.InputDataVerificationError'));
              return false;
            }
          }
          if (!this.isValidIpAddress(originIp)) {
            this.notifyError(this.$t('message.IsNotAValidHostnameOrIpFormat', [originIp]), this.$t('message.InputDataVerificationError'));
            return false;
          }

          // Final source validator
          if (!this.source_validator(originIp)) {
            this.notifyError(this.$t('message.IsNotAValidHostnameOrIpFormat', [originIp]), this.$t('message.InputDataVerificationError'));
            return false;
          }

          return true;
        });

        if (!originValidationIp) return;

        const cellsValidationPassed = data.every((datum) => {
          const shouldRequestOriginIpAndGroup = cleanInput(datum[COLUMN_KEYS.STATUS]).toLowerCase() === 'online';
          const isDomainValid = this.domain_validator(cleanInput(datum[COLUMN_KEYS.NAME]));

          if (shouldRequestOriginIpAndGroup && datum[COLUMN_KEYS.NAME].split('.')[0].length > 63) {
            if (!this.longDomainErrorShown) {
              this.longDomainErrorShown = true;
              this.flash(this.$t('message.DomainNameValidationError'), 'error', { "timeout": 5000 });
            }
            return false;
          }

          return isDomainValid && (!shouldRequestOriginIpAndGroup || this.source_validator(cleanInput(datum[COLUMN_KEYS.ORIGIN_IP])));
        });
        if (!cellsValidationPassed) {
          if (!this.longDomainErrorShown) {
            this.flash(this.$t('message.cell_value_validation_failed'), 'error', { "timeout": 5000 });
          }
          return;
        }

        this.mutatedData = data.map((datum) => {
          datum._id = Math.random();
          datum[COLUMN_KEYS.ORIGIN_IP] = datum[COLUMN_KEYS.ORIGIN_IP] ? cleanInput(datum[COLUMN_KEYS.ORIGIN_IP]) : '';
          datum[COLUMN_KEYS.SHADOW_DOMAINS] = datum[COLUMN_KEYS.SHADOW_DOMAINS] ? cleanInput(datum[COLUMN_KEYS.SHADOW_DOMAINS]) : '';
          datum[COLUMN_KEYS.REMARK] = datum[COLUMN_KEYS.REMARK] ? cleanInput(datum[COLUMN_KEYS.REMARK]) : '-';
          datum[COLUMN_KEYS.AUTO_GENERATE_CERT] = datum[COLUMN_KEYS.AUTO_GENERATE_CERT] ? cleanInput(datum[COLUMN_KEYS.AUTO_GENERATE_CERT]).toLowerCase() : 'false';
          return datum;
        });

        this.startImport();
        this.resetLang();
      };
      reader.readAsBinaryString(file);
    }
  }
};
</script>
